import { AsyncPipe } from '@angular/common'
import { Component, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core'
import { MatButtonModule, MatIconButton } from '@angular/material/button'
import { MatDialog } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatMenuModule } from '@angular/material/menu'
import { MatTooltip } from '@angular/material/tooltip'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { MatIconButtonSizesModule } from 'mat-icon-button-sizes'
import { Call, CallsService, MetricType } from '../../calls.service'
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component'
import { InfoDialogComponent } from '../../info-dialog/info-dialog.component'
import { MetricsService } from '../../metrics.service'
import { ToastService } from '../../toast.service'
import { UsersService } from '../../users.service'
import { EditorSettingsService } from '../editor/editor-settings.service'
import { EditorComponent } from '../editor/editor.component'
import { OutlineComponent } from '../outline/outline.component'
import { TiptapEditorComponent } from '../tiptap-editor/tiptap-editor.component'

@Component({
  selector: 'app-call-outputs-container',
  standalone: true,
  imports: [
    EditorComponent,
    OutlineComponent,
    TiptapEditorComponent,
    MatIcon,
    MatIconButton,
    TranslateModule,
    MatTooltip,
    MatMenuModule,
    MatButtonModule,
    AsyncPipe,
    MatIconButtonSizesModule,
  ],
  templateUrl: './call-outputs-container.component.html',
  styleUrl: './call-outputs-container.component.scss',
})
export class CallOutputsContainerComponent {
  @ViewChild(EditorComponent)
  editor!: EditorComponent

  @ViewChild(OutlineComponent)
  outlineComponent: OutlineComponent | undefined

  @Input({ required: true })
  call!: Call

  @Input({ required: true })
  canUserEdit!: boolean

  @Input({ required: true })
  canUserComment!: boolean

  @Output()
  openAgendaUpload = new EventEmitter<void>()

  @Output()
  openMeeting = new EventEmitter<void>()

  @Output()
  openDocument = new EventEmitter<{ resourceId: number; resourcePageNumber: number }>()

  get showExpandAll(): boolean {
    return this.outlineComponent?.showExpandAll() ?? false
  }

  currentTab: 'EDITOR' | 'OUTLINE' = 'EDITOR'
  useTiptapEditor = false

  get showCitations(): boolean {
    return this.editorSettingsService.showCitations
  }

  set showCitations(value: boolean) {
    this.editorSettingsService.setShowCitations(value)
  }

  get hasCitations(): boolean {
    return this.call.minutesCitations !== null
  }

  toggleCitations(): void {
    this.editorSettingsService.setShowCitations(!this.showCitations)
  }

  get showWordCount(): boolean {
    return this.editorSettingsService.showWordCount
  }

  toggleWordCount(): void {
    this.editorSettingsService.setShowWordCount(!this.showWordCount)
  }

  toastService = inject(ToastService)
  dialog = inject(MatDialog)
  translateService = inject(TranslateService)
  metricsService = inject(MetricsService)
  callsService = inject(CallsService)
  usersService = inject(UsersService)
  editorSettingsService = inject(EditorSettingsService)
  currentUser$ = this.usersService.currentUser$
  isEditorLockedByOtherUser = true

  async resetEditor() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('EDITOR.RESET_DIALOG_TITLE'),
        description: this.translateService.instant('EDITOR.RESET_DIALOG_DESCRIPTION'),
      },
    })
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.editor.reset()
      }
    })
  }

  showRevisionHistory() {
    this.editor.showRevisionHistory()
  }

  switchTab(tab: 'EDITOR' | 'OUTLINE') {
    this.currentTab = tab
    if (tab === 'OUTLINE') {
      this.metricsService.sendMetric(this.call.id, MetricType.CLICK_ON_OUTLINE_TAB)
    }
  }

  async exportDocument(format: 'pdf' | 'docx') {
    const contentType = this.currentTab === 'EDITOR' ? 'minutes' : 'outline'
    const infoDialogRef = this.dialog.open(InfoDialogComponent, {
      width: '400px',
      data: {
        title: this.translateService.instant(contentType === 'minutes' ? 'CALLS.MINUTES_EXPORT_DIALOG_TITLE' : 'CALLS.OUTLINE_EXPORT_DIALOG_TITLE'),
        displayLoadingSpinner: true,
        displayConfirmButton: false,
      },
    })
    const export$ = contentType === 'minutes' ? this.callsService.exportMinutes(this.call, format) : this.callsService.exportOutline(this.call, format)
    const subscription = export$.subscribe({
      error: () => {
        this.toastService.show('CALLS.EXPORT_FAILED')
        infoDialogRef.close()
      },
      complete: () => {
        infoDialogRef.close()
      },
    })
    infoDialogRef.afterClosed().subscribe(() => {
      subscription.unsubscribe()
    })
  }

  expandAll() {
    this.outlineComponent?.expandAll()
  }

  collapseAll() {
    this.outlineComponent?.collapseAll()
  }
}
